// Utils
import { featureConfig } from 'config/features';
import { IUser } from 'features/auth/authTypes';
import { Permission } from 'features/roles';

export interface AccessibleRoute {
  href: string;
  labelKey: string;
}

/**
 * Navigation items in sidebar order with their required permissions.
 * This mirrors the order in SidebarMain.tsx
 */
const NAVIGATION_ORDER: Array<{
  href: string;
  labelKey: string;
  requiredPermission?: Permission | Permission[];
  enabled: boolean;
}> = [
  {
    href: '/dashboard',
    labelKey: 'SIDEBAR_DASHBOARD',
    requiredPermission: [Permission.VIEW_PROJECT_BUDGETS, Permission.VIEW_EXPENSES],
    enabled: featureConfig.dashboard,
  },
  {
    href: '/tasks',
    labelKey: 'SIDEBAR_TASKS',
    requiredPermission: Permission.VIEW_ALL_TASKS,
    enabled: featureConfig.tasks,
  },
  {
    href: '/projects',
    labelKey: 'SIDEBAR_PROJECTS',
    requiredPermission: Permission.VIEW_PROJECTS,
    enabled: featureConfig.projects,
  },
  {
    href: '/work-orders',
    labelKey: 'SIDEBAR_WORK_ORDERS',
    requiredPermission: Permission.VIEW_WORK_ORDERS,
    enabled: featureConfig.workOrders,
  },
  {
    href: '/leads',
    labelKey: 'SIDEBAR_LEADS',
    requiredPermission: Permission.VIEW_LEADS,
    enabled: featureConfig.leads,
  },
  {
    href: '/location',
    labelKey: 'SIDEBAR_LOCATIONS',
    requiredPermission: Permission.VIEW_CLIENTS,
    enabled: featureConfig.leads,
  },
  {
    href: '/schedule',
    labelKey: 'SIDEBAR_SCHEDULE',
    requiredPermission: Permission.VIEW_SCHEDULE,
    enabled: featureConfig.schedule,
  },
  {
    href: '/inventory',
    labelKey: 'SIDEBAR_INVENTORY',
    requiredPermission: Permission.VIEW_INVENTORY,
    enabled: featureConfig.inventory,
  },
  {
    href: '/accounting',
    labelKey: 'SIDEBAR_ACCOUNTING',
    requiredPermission: Permission.VIEW_FINANCIAL_REPORTS,
    enabled: featureConfig.accounting,
  },
];

/**
 * Check if a user has a specific permission or all permissions in an array.
 * SuperAdmins have all permissions. Other users must have the permission in their array.
 */
const hasPermission = (
  user: IUser,
  permission: Permission | Permission[] | undefined,
): boolean => {
  // No permission required
  if (!permission) return true;

  // SuperAdmin has all permissions
  if (user.superAdmin === true) return true;

  // Check user's permissions array
  const userPermissions = user.permissions ?? [];

  // Check if user has admin-level permissions (MANAGE_USERS + MANAGE_PERMISSIONS)
  const isAdmin = userPermissions.includes(Permission.MANAGE_USERS) &&
                  userPermissions.includes(Permission.MANAGE_PERMISSIONS);
  if (isAdmin) return true;

  // If it's an array, user must have ALL permissions
  if (Array.isArray(permission)) {
    return permission.every((p) => userPermissions.includes(p));
  }

  // Single permission check
  return userPermissions.includes(permission);
};

/**
 * Get the first accessible route for a user based on their permissions.
 * Follows the sidebar navigation order.
 *
 * @param user - The authenticated user (IUser)
 * @returns AccessibleRoute with href and labelKey, or null if no route is accessible
 */
export const getFirstAccessibleRoute = (user: IUser): AccessibleRoute | null => {
  for (const navItem of NAVIGATION_ORDER) {
    // Skip disabled features
    if (!navItem.enabled) continue;

    // Check if user has permission
    if (hasPermission(user, navItem.requiredPermission)) {
      return {
        href: navItem.href,
        labelKey: navItem.labelKey,
      };
    }
  }

  // Fallback: no accessible route found
  return null;
};

/**
 * Get the first accessible route URL for a user.
 * Convenience function that returns just the URL string.
 *
 * @param user - The authenticated user (IUser)
 * @returns The accessible route URL, or '/dashboard' as ultimate fallback
 */
export const getFirstAccessibleRouteUrl = (user: IUser): string => {
  const route = getFirstAccessibleRoute(user);
  return route?.href ?? '/dashboard';
};

